﻿import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "@mui/material/Tooltip";
import { useQuery } from "@tanstack/react-query";
import "react-datepicker/dist/react-datepicker.css";

import { baseCrumbClasses } from "./Breadcrumbs";
import { settingsAjax } from "../../ajax/SettingsAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";

interface DateSelectCrumbProps {
  selectedDate: DateTime;
  setSelectedDate: (date: DateTime) => void;
}

type ShiftExistFlags = { day: boolean; night: boolean };

function monthBounds(d: Date) {
  const monthStart = DateTime.fromJSDate(d).startOf("month");
  const fromIso = monthStart.minus({ days: 7 }).toISODate()!;
  const toIso = monthStart.endOf("month").plus({ days: 7 }).toISODate()!;
  const key = monthStart.toFormat("yyyy-LL");
  return { key, fromIso, toIso };
}

export const DateSelectCrumb = ({ selectedDate, setSelectedDate }: DateSelectCrumbProps) => {
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [viewedMonth, setViewedMonth] = useState<Date>(
    () => selectedDate
      ? new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day)
      : new Date()
  );

  useEffect(() => {
    if (calendarVisible && selectedDate) {
      setViewedMonth(new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day));
    }
  }, [calendarVisible]);

  const { key, fromIso, toIso } = monthBounds(viewedMonth);

  const { data: byDate = {}, isFetching } = useQuery({
    queryKey: ["shiftExistence", key],
    queryFn: async () => {
      const res = await settingsAjax.getShiftExistence(fromIso, toIso);
      if (res instanceof ErrorResponse) throw new Error("Failed to fetch shift existence");

      const result: Record<string, ShiftExistFlags> = {};
      res.days.forEach(d => {
        const iso = d.date.toISODate();
        if (!iso) return;
        result[iso] = { day: d.day, night: d.night };
      });
      return result;
    },
    enabled: calendarVisible,
    staleTime: 15 * 1000,
    gcTime: 30 * 1000
  });

  const onPick = (date: Date | null) => {
    if (!date) return;
    const sys = DateTime.fromJSDate(date, { zone: "system" });
    setSelectedDate(DateTime.utc(sys.year, sys.month, sys.day));
  };

  const selectedForPicker = useMemo(() => {
    if (!selectedDate) return null;
    return new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day, 0, 0, 0, 0);
  }, [selectedDate]);

  const isDateAllowed = useCallback(
    (date: Date) => {
      const iso = DateTime.fromJSDate(date).toISODate()!;
      const flags = byDate[iso];
      return !!flags && (flags.day || flags.night);
    },
    [byDate]
  );

  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    if (isDateAllowed(date)) return <span>{day}</span>;

    return (
      <Tooltip
        title="No shifts exist for this date. Create them in Operations List first."
        arrow
        placement="top"
      >
        <div className="text-gray-200 cursor-not-allowed h-full w-full">{day}</div>
      </Tooltip>
    );
  };

  return (
    <>
      <button
        className={`${baseCrumbClasses} text-sm md:text-base p-2 lg:px-6`}
        onMouseEnter={() => setCalendarVisible(true)}
        onClick={() => setCalendarVisible(v => !v)}
      >
        {selectedDate ? selectedDate.toISODate() : "Select a Date"}
      </button>

      {calendarVisible && (
        <div className={`absolute mt-2 left-0 top-8 z-20 transition-opacity ${isFetching ? "opacity-60" : ""}`}>
          <div
            className={`absolute z-30 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl transition ${isFetching ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
          >
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>

          <DatePicker
            disabled={isFetching}
            selected={selectedForPicker}
            onClickOutside={() => setCalendarVisible(false)}
            renderDayContents={renderDayContents}
            closeOnScroll
            onChange={onPick}
            inline
            filterDate={(d) => (isFetching ? true : isDateAllowed(d))}
            onMonthChange={setViewedMonth}
            onYearChange={setViewedMonth}
          />
        </div>
      )}
    </>
  );
};